import type { Translate } from "@similarweb/i18n-provider";
import { useTranslate } from "@similarweb/i18n-provider";
import type { IPreset } from "@similarweb/ui-components";

import { EPresetOption } from "app/constants/websiteKeywordsPresetOptions";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

export { EPresetOption };

export type PresetWithDisabled<T extends string | number> = IPreset<T> & { disabled?: boolean };

const tooltipByTabResolver = (
    translate: Translate,
    presetOption: EPresetOption.Cannibalization | EPresetOption.HighTraffic,
    selectedPageTab: keyof typeof EKeywordTabs,
): string => {
    const tooltipByTabMap = {
        [EPresetOption.Cannibalization]: {
            [EKeywordTabs[EKeywordTabs.Total]]: translate(
                "website.keywords.2.0.page.table.overview.competitive.filter.cannibalization.tooltip.total",
            ),
            [EKeywordTabs[EKeywordTabs.Organic]]: translate(
                "website.keywords.2.0.page.table.overview.competitive.filter.cannibalization.tooltip.organic",
            ),
            [EKeywordTabs[EKeywordTabs.Paid]]: translate(
                "website.keywords.2.0.page.table.overview.competitive.filter.cannibalization.tooltip.paid",
            ),
        },
        [EPresetOption.HighTraffic]: {
            [EKeywordTabs[EKeywordTabs.Total]]: translate(
                "website.keywords.2.0.page.table.overview.competitive.filter.high_traffic.tooltip.total",
            ),
            [EKeywordTabs[EKeywordTabs.Organic]]: translate(
                "website.keywords.2.0.page.table.overview.competitive.filter.high_traffic.tooltip.organic",
            ),
            [EKeywordTabs[EKeywordTabs.Paid]]: translate(
                "website.keywords.2.0.page.table.overview.competitive.filter.high_traffic.tooltip.paid",
            ),
        },
    };
    return tooltipByTabMap[presetOption][selectedPageTab];
};

export const getPresetsBoxesConfig = (
    translate: Translate,
    primaryDomain: string,
    isCompare: boolean,
    selectedPageTab: string,
    isWorldWide: boolean,
): PresetWithDisabled<EPresetOption>[] =>
    isCompare
        ? [
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.opportunities"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.opportunities.tooltip",
                      { primaryDomain },
                  ),
                  id: EPresetOption.Opportunities,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.losses"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.losses.tooltip",
                      { primaryDomain },
                  ),
                  id: EPresetOption.Losses,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.wins"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.wins.tooltip",
                      { primaryDomain },
                  ),
                  id: EPresetOption.Wins,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.highly_competitive"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.highly_competitive.tooltip",
                      { primaryDomain },
                  ),
                  id: EPresetOption.HighlyCompetitive,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.competitor_winning"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.competitor_winning.tooltip",
                      { primaryDomain },
                  ),
                  id: EPresetOption.Overlapping,
              },
          ]
        : [
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.cannibalization"),
                  tooltipContent: tooltipByTabResolver(
                      translate,
                      EPresetOption.Cannibalization,
                      selectedPageTab as keyof typeof EKeywordTabs,
                  ),
                  id: EPresetOption.Cannibalization,
                  disabled: isWorldWide,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.long_tail"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.long_tail.tooltip",
                  ),
                  id: EPresetOption.LongTail,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.serp_features"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.serp_features.tooltip",
                  ),
                  id: EPresetOption.SerpFeatures,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.high_traffic"),
                  tooltipContent: tooltipByTabResolver(
                      translate,
                      EPresetOption.HighTraffic,
                      selectedPageTab as keyof typeof EKeywordTabs,
                  ),
                  id: EPresetOption.HighTraffic,
                  disabled: isWorldWide,
              },
              {
                  title: translate("website.keywords.2.0.page.table.overview.competitive.filter.low_potential"),
                  tooltipContent: translate(
                      "website.keywords.2.0.page.table.overview.competitive.filter.low_potential.tooltip",
                  ),
                  id: EPresetOption.LowPotential,
              },
          ];

export const usePresetsBoxesConfig = (
    primaryDomain: string,
    isCompare: boolean,
    selectedPageTab: string,
    isWorldWide: boolean,
): PresetWithDisabled<EPresetOption>[] => {
    const { translate } = useTranslate();
    return getPresetsBoxesConfig(translate, primaryDomain, isCompare, selectedPageTab, isWorldWide);
};
